import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const WorkspaceJoinRequestListDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"WorkspaceJoinRequestList"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"orgId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"organization"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"orgId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"accessRequests"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"idMemberRequestor"}},{"kind":"Field","name":{"kind":"Name","value":"signature"}},{"kind":"Field","name":{"kind":"Name","value":"timestamp"}}]}},{"kind":"Field","name":{"kind":"Name","value":"collaborators"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}}]}},{"kind":"Field","name":{"kind":"Name","value":"prefs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"newLicenseInviteRestrictUrl"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"WorkspaceJoinRequestList","document":WorkspaceJoinRequestListDocument}} as const;
export type WorkspaceJoinRequestListQueryVariables = Types.Exact<{
  orgId: Types.Scalars['ID']['input'];
}>;


export type WorkspaceJoinRequestListQuery = (
  { __typename: 'Query' }
  & { organization?: Types.Maybe<(
    { __typename: 'Organization' }
    & Pick<Types.Organization, 'id'>
    & { accessRequests?: Types.Maybe<Array<(
      { __typename: 'AccessRequestItem' }
      & Pick<Types.AccessRequestItem, 'id' | 'idMemberRequestor' | 'signature' | 'timestamp'>
    )>>, collaborators: Array<(
      { __typename: 'Collaborator' }
      & Pick<Types.Collaborator, 'id'>
    )>, prefs: (
      { __typename: 'Organization_Prefs' }
      & Pick<Types.Organization_Prefs, 'newLicenseInviteRestrictUrl'>
    ) }
  )> }
);

/**
 * __useWorkspaceJoinRequestListQuery__
 *
 * To run a query within a React component, call `useWorkspaceJoinRequestListQuery` and pass it any options that fit your needs.
 * When your component renders, `useWorkspaceJoinRequestListQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useWorkspaceJoinRequestListQuery({
 *   variables: {
 *      orgId: // value for 'orgId'
 *   },
 * });
 */
export function useWorkspaceJoinRequestListQuery(
  baseOptions: TrelloQueryHookOptions<
    WorkspaceJoinRequestListQuery,
    WorkspaceJoinRequestListQueryVariables
  > &
    (
      | { variables: WorkspaceJoinRequestListQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: WorkspaceJoinRequestListDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    WorkspaceJoinRequestListQuery,
    WorkspaceJoinRequestListQueryVariables
  >(WorkspaceJoinRequestListDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useWorkspaceJoinRequestListLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    WorkspaceJoinRequestListQuery,
    WorkspaceJoinRequestListQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    WorkspaceJoinRequestListQuery,
    WorkspaceJoinRequestListQueryVariables
  >(WorkspaceJoinRequestListDocument, options);
}
export function useWorkspaceJoinRequestListSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        WorkspaceJoinRequestListQuery,
        WorkspaceJoinRequestListQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    WorkspaceJoinRequestListQuery,
    WorkspaceJoinRequestListQueryVariables
  >(WorkspaceJoinRequestListDocument, options);
}
export type WorkspaceJoinRequestListQueryHookResult = ReturnType<
  typeof useWorkspaceJoinRequestListQuery
>;
export type WorkspaceJoinRequestListLazyQueryHookResult = ReturnType<
  typeof useWorkspaceJoinRequestListLazyQuery
>;
export type WorkspaceJoinRequestListSuspenseQueryHookResult = ReturnType<
  typeof useWorkspaceJoinRequestListSuspenseQuery
>;
export type WorkspaceJoinRequestListQueryResult = Apollo.QueryResult<
  WorkspaceJoinRequestListQuery,
  WorkspaceJoinRequestListQueryVariables
>;
